import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { Component, computed, effect, HostListener, inject, input, output, TemplateRef, viewChild, ViewEncapsulation } from '@angular/core';
import { DraggableEnpsComponent } from '@common/components/base/draggable-enps-component';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { FieldDefValidations } from '@common/enums/enums';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { VisualStateService } from '@common/services/visual-state-service';
import { selectViewStateCustomCardItems, selectViewStateSearchString } from '@common/store/navigation/selectors/viewstate.selectors';
import { Store } from '@ngrx/store';
import { Staff } from '@src/suite/common/data-models/service/staff';
import { HighlightPipe } from '@src/suite/common/pipes/highlight';
import { selectClientTimeZone, selectPersonalTimeZone } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { selectStoryTemplates } from '@src/suite/common/store/records/records.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { getTopicSecurityPrivsWrite } from '@src/suite/common/utils/record-utils';
import { getItemString } from '@suite/common/utils/util';

@Component({
    selector: 'st-topic-item-condensed',
    templateUrl: './topic-item-condensed.html',
    styleUrls: ['./topic-item-condensed.scss'],
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-topic-item-condensed',
    },
})
export class TopicItemCondensedComponent extends DraggableEnpsComponent {
    private store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    visualStateService = inject(VisualStateService);
    languageSettings = inject(LanguageSettings);
    highlightPipe = inject(HighlightPipe);
    data = input<StorytellingTopicRecord>(null);
    isReadOnly = input(false);
    preview = input(false);
    isPublication = input(false);
    personalTimeZone = this.store.selectSignal(selectPersonalTimeZone);
    clientTimeZone = this.store.selectSignal(selectClientTimeZone);

    viewStateCustomCondensedItems$ = this.store.select(selectViewStateCustomCardItems);
    i18n = this.store.selectSignal(selectI18N);
    searchString = this.store.selectSignal(selectViewStateSearchString);
    storyTemplates = this.store.selectSignal(selectStoryTemplates);
    topicItemClicked = output();
    assignmentItemClicked = output<StorytellingAssignmentRecord>();
    storyItemClicked = output<StorytellingStoryRecord>();
    deletePublicationEmitter = output();
    assignmentsOverlay = viewChild('assignmentsOverlay', { read: TemplateRef });
    assignmentsOverlayEl = viewChild('assignmentsOverlayEl', { read: CdkConnectedOverlay });
    storyOwnerEl = viewChild('storyOwnerEl', { read: CdkConnectedOverlay });

    public readonly FieldDefValidations = FieldDefValidations;
    pstFieldNames = PSTFieldNames;
    isListOpen: boolean = false;
    isAssignmentsPanelOpen = false;
    public readonly getItemString = getItemString;
    public itemStringCache = new Map<string, string>();
    notAssigned: string = this.i18n()[i18nKeys.PSTWebNotAssignedOption];

    tags = computed(() => {
        return this.data()
            .Tags.map(tag => {
                return tag.displayText;
            })
            .join(', ');
    });

    description = computed(() => {
        return this.data()?.Description || this.globalTablesService.FieldDefsDict[this.pstFieldNames.Description]?.DefaultCaption;
    });

    topicGroupsItem = computed(() => {
        return this.data()?.PSTGroup?.join(', ');
    });

    extraOwnerCount = computed(() => {
        return this.data()?.PSTTopicOwner?.length - 1;
    });

    tooltipUsers = computed(() => {
        const users = this.data().PSTTopicOwner?.slice(0, 5) || [];
        return this.allUsers.filter(option => users.includes(option.UserID)).map(item => item);
    });

    topicEditable = computed(() => {
        return !this.isReadOnly() && getTopicSecurityPrivsWrite(this.data()) && !this.data()?.Path?.isWasteBinPath;
    });

    currentTimeZone = computed(() => {
        return this.personalTimeZone() ? this.personalTimeZone() : this.clientTimeZone();
    });

    get shouldShowCustomFields(): boolean {
        return !this.visualStateService.isLessThanMedium;
    }

    get allUsers(): Staff[] {
        return Object.values(this.globalTablesService.Staff);
    }

    constructor() {
        super();
        effect(() => {
            if (this.data()) {
                this.itemStringCache.clear();
            }
        });
    }

    @HostListener('click')
    public onClick() {
        if (this.isReadOnly()) {
            return;
        }
        this.topicItemClicked.emit();
    }

    updateTopic(value: any, fieldId: string) {
        const fieldDef = [{ value, key: fieldId }];
        // TODO: not cool to have this title logic here
        if (fieldId === PSTFieldNames.Title) {
            this.data().Title = value.trim();
            this.data().Slug = value.trim();
            fieldDef.push({ value: value, key: PSTFieldNames.Slug });
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }

    getInnerProp(item: FieldDef) {
        const val = this.data()?.[item.ID.split('-')[0]];
        if (val) return val[item.ComplexPropInnerId];
        return '';
    }

    assignmentsPanelAttached() {
        this.panelAttached(this.assignmentsOverlayEl());
    }

    storyOwnerPanelAttached() {
        this.panelAttached(this.storyOwnerEl());
    }

    private panelAttached(overlayEl: CdkConnectedOverlay = null) {
        const overlayElement = overlayEl?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseleave', () => {
                this.isAssignmentsPanelOpen = false;
                this.isListOpen = false;
            });
        }
    }

    showStaffTooltip(value) {
        const users = value || [];
        if (users?.length) {
            this.isListOpen = true;
        }
    }

    onStoryOwnerMouseOut(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-staff-id-list-tooltip-content')) {
            this.isListOpen = false;
        }
    }

    public deletePublication(event: any) {
        event.stopPropagation();
        this.deletePublicationEmitter.emit();
    }

    focusDuplicate() {
        this.topicItemClicked.emit();
    }

    openAssignment(assignment: StorytellingAssignmentRecord) {
        this.assignmentItemClicked.emit(assignment);
    }

    openStory(story: StorytellingStoryRecord) {
        this.storyItemClicked.emit(story);
    }

    async createStory(event: { templateProgramId: string; templateGuids: string[] }) {
        this.store.dispatch(PlaybookApiActions.createStoryFromTemplate({ topic: this.data(), templateProgramId: event.templateProgramId, templateGuids: event.templateGuids }));
    }

    async createAssignment(type: string) {
        this.store.dispatch(PlaybookApiActions.createAssignment({ topic: this.data(), assignmentType: type }));
    }
}
